import React, { type FC, useState } from 'react'
import { Box, ButtonGroup, FormControlLabel } from '@mui/material'
import { Visibility } from '@mui/icons-material'

import { Button, Checkbox, type DropdownSize, TextField } from '../common'
import { DefaultIcon } from './DefaultIcon'
import { SplitButtonControl } from './SplitButtonControl'

const inputExampleStyle = { width: 320, maxWidth: '100%' } as const

type PlaygroundToggle = Readonly<{ label: string, checked: boolean, onChange: (checked: boolean) => void }>
type PlaygroundState = 'DEFAULT' | 'DISABLED' | 'ERROR'

const stateOptions: readonly PlaygroundState[] = ['DEFAULT', 'DISABLED', 'ERROR']
const sizeOptions: readonly DropdownSize[] = ['small', 'medium']

const playgroundStyle = { display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-start' } as const
const playgroundControlsStyle = { display: 'flex', flexDirection: 'column' } as const

const PlaygroundControls: FC<Readonly<{ toggles: readonly PlaygroundToggle[] }>> = ({ toggles }) => (
    <Box sx={playgroundControlsStyle}>
        {toggles.map(({ label, checked, onChange }) => (
            <FormControlLabel
                key={label}
                label={label}
                control={<Checkbox checked={checked} onChange={(_, value) => onChange(value)} />}
            />
        ))}
    </Box>
)

const PlaygroundStateControl: FC<Readonly<{ state: PlaygroundState, onChange: (state: PlaygroundState) => void }>> = ({ state, onChange }) => (
    <ButtonGroup aria-label="state" sx={{ mb: 4 }}>
        {stateOptions.map((option) => (
            <Button
                key={option}
                variant={option === state ? 'primary' : 'primaryOutline'}
                onClick={() => onChange(option)}
            >
                {option}
            </Button>
        ))}
    </ButtonGroup>
)

export const Inputs: FC = () => {
    const [state, setState] = useState<PlaygroundState>('DEFAULT')
    const [size, setSize] = useState<DropdownSize>('medium')
    const [showLabel, setShowLabel] = useState<boolean>(true)
    const [showRequired, setShowRequired] = useState<boolean>(true)
    const [showOptional, setShowOptional] = useState<boolean>(true)
    const [showHelpIcon, setShowHelpIcon] = useState<boolean>(true)
    const [showMessage, setShowMessage] = useState<boolean>(true)
    const [showLeadingIcon, setShowLeadingIcon] = useState<boolean>(true)
    const [showEndIcon, setShowEndIcon] = useState<boolean>(true)

    const toggles: readonly PlaygroundToggle[] = [
        { label: 'showLabel', checked: showLabel, onChange: setShowLabel },
        { label: 'showRequired', checked: showRequired, onChange: setShowRequired },
        { label: 'showOptional', checked: showOptional, onChange: setShowOptional },
        { label: 'showHelpIcon', checked: showHelpIcon, onChange: setShowHelpIcon },
        { label: 'showMessage', checked: showMessage, onChange: setShowMessage },
        { label: 'showLeadingIcon', checked: showLeadingIcon, onChange: setShowLeadingIcon },
        { label: 'showEndIcon', checked: showEndIcon, onChange: setShowEndIcon },
    ]

    const disabled = state === 'DISABLED'
    const error = state === 'ERROR'

    return (
        <Box sx={playgroundStyle}>
            <Box sx={inputExampleStyle}>
                <TextField
                    label={showLabel ? 'Label' : undefined}
                    placeholder="Type to search..."
                    helperText={showMessage || error ? 'This is a hint text to help user.' : undefined}
                    helperTextIcon={<DefaultIcon />}
                    required={showRequired}
                    optional={showOptional}
                    helpTooltip={showHelpIcon ? 'Helpful description' : undefined}
                    startIcon={showLeadingIcon ? <DefaultIcon /> : undefined}
                    endIcon={showEndIcon ? <Visibility /> : undefined}
                    disabled={disabled}
                    error={error}
                    defaultValue={disabled ? 'Disabled value' : undefined}
                    size={size}
                    fullWidth
                />
            </Box>
            <Box sx={playgroundControlsStyle}>
                <PlaygroundStateControl state={state} onChange={setState} />
                <SplitButtonControl ariaLabel="Input size" testIdPrefix="inputs-storybook-size" option={size} options={sizeOptions} onChange={setSize} />
                <PlaygroundControls toggles={toggles} />
            </Box>
        </Box>
    )
}
